import { WakeUp } from 'common/interface/props';
import { ICoreOutput } from 'common/common.interface';

/**
 * @alias 작업 상세 검색 엑셀 다운로드
 */
export interface JobDetailSearchExcelInput {
  /**
   * @alias 시작 날짜
   */
  startDate: string;
  /**
   * @alias 종료 날짜
   */
  endDate: string;
  /**
   * @alias 솔루션 타입
   */
  solType: WakeUp;
  /**
   * @alias 검색 타입
   */
  searchType: string;
  /**
   * @alias 검색 값
   */
  searchValue: string;
  /**
   * @alias 다운로드 사유
   */
  downloadReason: string;
  /**
   * @alias 암호화 비밀번호
   */
  password: string;
  /**
   * @alias 통합 관리자 여부
   */
  isIntegrated?: boolean;
}

/**
 * @alias 작업 상세 결과 엑셀 다운로드
 */
export interface DetailJobExcelInput extends JobDetailSearchExcelInput {}

// ---------------------------------------
// 공지사항
// ---------------------------------------

/**
 * @alias 공지사항 타입
 */
export type NoticeType = 'INFORMATION' | 'URGENT';

/**
 * @alias 정보 박스 아이템
 */
export interface InfoBoxItem {
  /**
   * @alias 라벨
   */
  label: string;
  /**
   * @alias 값
   */
  value: string;
}

/**
 * @alias 정보 박스 링크
 */
export interface InfoBoxLink {
  /**
   * @alias 링크 텍스트
   */
  text: string;
  /**
   * @alias 링크 URL
   */
  url: string;
}

/**
 * @alias 정보 박스
 */
export interface InfoBox {
  /**
   * @alias 정보 박스 아이템 목록
   */
  items: InfoBoxItem[];
  /**
   * @alias 경고 메시지
   */
  warning?: string | string[];
  /**
   * @alias 링크 목록
   */
  links?: InfoBoxLink[];
}

/**
 * @alias 공지사항
 */
export interface NoticeItem {
  /**
   * @alias 공지사항 ID
   */
  noticeId: number;
  /**
   * @alias 제목
   */
  title: string;
  /**
   * @alias 부제목
   */
  subtitle?: string;
  /**
   * @alias 내용
   */
  content: string | string[];
  /**
   * @alias 정보 박스
   */
  infoBox?: InfoBox;
  /**
   * @alias 공지 시작일
   */
  startDate: string;
  /**
   * @alias 공지 종료일
   */
  endDate: string;
  /**
   * @alias 로그인 차단 시작일 (URGENT 타입일 때만 사용)
   */
  blockStartDate?: string;
  /**
   * @alias 로그인 차단 종료일 (URGENT 타입일 때만 사용)
   */
  blockEndDate?: string;
  /**
   * @alias 노출 여부
   */
  visibleYn: boolean;
  /**
   * @alias 솔루션 타입
   */
  solType: string;
  /**
   * @alias 공지 타입
   */
  noticeType: NoticeType;
  /**
   * @alias 생성일
   */
  createdAt: string;
  /**
   * @alias 수정일
   */
  updatedAt: string;
  /**
   * @alias 작성자
   */
  authorId: string;
  /**
   * @alias 공지 순서
   */
  order: number;
}

/**
 * @alias 공지사항 목록 조회 Input
 */
export interface GetNoticeListInput {
  /**
   * @alias 솔루션 타입
   */
  solType?: string;
  /**
   * @alias 솔루션 타입에 전체 포함할지
   */
  includeAll?: boolean;
  /**
   * @alias 생성일 시작 (YYYY-MM-DD)
   */
  createdAtFrom?: string;
  /**
   * @alias 생성일 종료 (YYYY-MM-DD)
   */
  createdAtTo?: string;
  /**
   * @alias 노출 여부
   */
  visibleYn?: boolean;
  /**
   * @alias 검색 타입
   */
  searchType?: 'title' | 'subtitle' | 'content' | 'all';
  /**
   * @alias 검색 키워드
   */
  searchKeyword?: string;
  /**
   * @alias 페이지 번호
   */
  page?: number;
  /**
   * @alias 페이지 사이즈
   */
  pageSize?: number;
}

/**
 * @alias 공지사항 목록 조회 Output
 */
export interface GetNoticeListOutput extends ICoreOutput {
  /**
   * @alias 공지사항 목록
   */
  notices?: NoticeItem[];
  /**
   * @alias 전체 개수
   */
  totalCount?: number;
  /**
   * @alias 전체 페이지
   */
  totalPage?: number;
  /**
   * @alias 현재 페이지
   */
  currentPage?: number;
  /**
   * @alias 페이지 사이즈
   */
  pageSize?: number;
}

/**
 * @alias 공지사항 단건 조회 Input
 */
export interface GetNoticeInput {
  /**
   * @alias 공지사항 ID
   */
  noticeId: number;
}

/**
 * @alias 공지사항 단건 조회 Output
 */
export interface GetNoticeOutput extends ICoreOutput {
  /**
   * @alias 공지사항
   */
  notice?: NoticeItem;
}

/**
 * @alias 공지사항 생성 Input
 */
export interface CreateNoticeInput {
  /**
   * @alias 제목
   */
  title: string;
  /**
   * @alias 부제목
   */
  subtitle?: string;
  /**
   * @alias 내용
   */
  content: string | string[];
  /**
   * @alias 정보 박스
   */
  infoBox?: InfoBox;
  /**
   * @alias 공지 시작일
   */
  startDate: string;
  /**
   * @alias 공지 종료일
   */
  endDate: string;
  /**
   * @alias 로그인 차단 시작일 (URGENT 타입일 때만 사용)
   */
  blockStartDate?: string;
  /**
   * @alias 로그인 차단 종료일 (URGENT 타입일 때만 사용)
   */
  blockEndDate?: string;
  /**
   * @alias 노출 여부
   */
  visibleYn?: boolean;
  /**
   * @alias 솔루션 타입
   */
  solType: string;
  /**
   * @alias 공지 타입
   */
  noticeType: NoticeType;
  /**
   * @alias 작성자 ID
   */
  userId: number;
}

/**
 * @alias 공지사항 생성 Output
 */
export interface CreateNoticeOutput extends ICoreOutput {
  /**
   * @alias 생성된 공지사항
   */
  notice?: NoticeItem;
}

/**
 * @alias 공지사항 수정 Input
 */
export interface UpdateNoticeInput {
  /**
   * @alias 공지사항 ID
   */
  noticeId: number;
  /**
   * @alias 제목
   */
  title?: string;
  /**
   * @alias 부제목
   */
  subtitle?: string;
  /**
   * @alias 내용
   */
  content?: string | string[];
  /**
   * @alias 정보 박스
   */
  infoBox?: InfoBox | null;
  /**
   * @alias 공지 시작일
   */
  startDate?: string;
  /**
   * @alias 공지 종료일
   */
  endDate?: string;
  /**
   * @alias 로그인 차단 시작일 (URGENT 타입일 때만 사용)
   */
  blockStartDate?: string;
  /**
   * @alias 로그인 차단 종료일 (URGENT 타입일 때만 사용)
   */
  blockEndDate?: string;
  /**
   * @alias 노출 여부
   */
  visibleYn?: boolean;
  /**
   * @alias 솔루션 타입
   */
  solType?: string;
  /**
   * @alias 공지 타입
   */
  noticeType?: NoticeType;
  /**
   * @alias 작성자 ID
   */
  userId: number;
}

/**
 * @alias 공지사항 수정 Output
 */
export interface UpdateNoticeOutput extends ICoreOutput {
  /**
   * @alias 수정된 공지사항
   */
  notice?: NoticeItem;
}

/**
 * @alias 공지사항 삭제 Input
 */
export interface DeleteNoticeInput {
  /**
   * @alias 공지사항 ID
   */
  noticeId: number;
}

/**
 * @alias 공지사항 삭제 Output
 */
export interface DeleteNoticeOutput extends ICoreOutput {}

/**
 * @alias 공지사항 노출 여부 변경 Output
 */
export interface ToggleNoticeVisibilityOutput extends ICoreOutput {}

// ---------------------------------------
// 쿠팡 문의용 로그
// ---------------------------------------

/** 작업 상세 컬럼명: 쿠팡 Wing 임시 상품 코드 (문의 API 호출 시 사용, 테이블에는 미노출) */
export const COUPANG_WING_TEMP_PROD_CODE_FOR_INQUIRY_KEY = '쿠팡 Wing 임시 상품 코드';

/** 작업 상세 컬럼명: 행의 작업번호 (단건 API 호출 시 jobId) */
export const COUPANG_ROW_JOB_ID_KEY = '문의용 작업번호';

/** 작업 상세 테이블에서 숨길 컬럼 (문의용으로만 사용, 사용자에게 미노출) */
export const COUPANG_JOB_DETAIL_HIDDEN_COLUMNS = [
  '쿠팡 Wing 임시 상품등록 성공 여부',
  '쿠팡 Wing 임시 상품등록 실패 메시지',
  '쿠팡 Wing 임시 상품 코드',
] as const;

/**
 * @alias 쿠팡 문의용 로그 한 건
 */
export interface CoupangInquiryLogItem {
  /**
   * @alias 쿠팡 Wing 임시 상품 코드
   */
  tempProdCode: string;
  /**
   * @alias 쿠팡 Wing 성공 실패 코드
   */
  coupangCode: string;
  /**
   * @alias 요청 내용
   */
  request: string;
  /**
   * @alias 응답 내용
   */
  response: string;
  /**
   * @alias 생성일
   */
  createdAt: string;
}

/**
 * @alias 쿠팡 문의용 로그 단건 조회 Output
 */
export interface GetCoupangInquiryLogOutput extends ICoreOutput {
  /**
   * @alias 쿠팡 문의용 로그
   */
  log?: CoupangInquiryLogItem;
}
